import { Component, h, Prop, State, Host, Listen, Watch } from "@stencil/core";
import {
  NavigationProps,
  NavigationState,
  NavigationItems,
} from "./f-navigation-model";
import config from "../../../../environment/environment-config";
import { startWatchingFocusOutAndClick } from "@globals/helpers/focus-helpers";
import { isTabletLandscape } from "@globals/viewports";

@Component({
  tag: "f-navigation",
  styleUrl: "f-navigation.scss",
  shadow: true,
})
export class Navigation {
  @Prop() navigationItems: NavigationProps["navigationItems"];
  @Prop() useContextPath: NavigationProps["useContextPath"] = true;
  @Prop() teaserLabel: NavigationProps["teaserLabel"] = "Overview";
  @Prop() externalLinkHiddenText: NavigationProps["externalLinkHiddenText"] =
    "Opens in a new window";

  @State() activeElementLvl1: NavigationState["activeElementLvl1"] = "";
  @State() activeElementLvl2: NavigationState["activeElementLvl2"] = "";
  @State() activeElementLvl3: NavigationState["activeElementLvl3"] = "";
  @State() isMobileMenuOpen: NavigationState["isMobileMenuOpen"] = false;
  @State() currentHeight;

  //=============== NAVIGATION CLOSE/OPEN =============

  private navMobileLayer: HTMLElement;
  private navLayersLvl2 = {};
  private navLayersLvl3 = {};
  private navLayersLvl4 = {};
  private closeNavigationController: AbortController;

  private linksLvl1 = {};
  private linksLvl2 = {};
  private linksLvl3 = {};

  private addController(focusElement, closingElement) {
    this.closeNavigationController?.abort();
    setTimeout(() => {
      this.closeNavigationController = new AbortController();
      startWatchingFocusOutAndClick(
        focusElement,
        this.closeNavigationController,
        closingElement
      );
    }, 0);
  }

  @Watch("isMobileMenuOpen")
  watchStateHandler(isOpen: boolean) {
    isTabletLandscape() &&
      (document.body.style.overflowX = isOpen ? "hidden" : "");
  }

  private resizeToTablet: boolean;

  @Listen("resize", { target: "window" })
  handleResize() {
    // resize form desktop to tablet
    if (isTabletLandscape() && this.resizeToTablet) {
      this.resizeToTablet = false;
      if (this.activeElementLvl3 !== "") {
        this.addController(
          this.navLayersLvl4[this.activeElementLvl3],
          this.closeMenuLvl4.bind(this)
        );
      } else if (this.activeElementLvl2 !== "") {
        this.addController(
          this.navLayersLvl3[this.activeElementLvl2],
          this.closeMenuLvl3.bind(this)
        );
      }
      if (this.isMobileMenuOpen) {
        document.body.style.overflowX = "hidden";
      }
    }
    // resize form tablet to desktop
    else if (!isTabletLandscape() && !this.resizeToTablet) {
      this.resizeToTablet = true;
      if (this.activeElementLvl3 !== "") {
        this.addController(
          this.navLayersLvl3[this.activeElementLvl2],
          this.closeMenuLvl3.bind(this)
        );
      } else if (this.activeElementLvl2 !== "") {
        this.addController(
          this.navLayersLvl2[this.activeElementLvl1],
          this.closeMenuLvl2.bind(this)
        );
      }
      if (this.isMobileMenuOpen) {
        document.body.style.overflowX = "";
      }
    }
  }

  private setNavigationHeight = (path: string): void => {
    if (!path) {
      this.currentHeight = this.initialHeight + "rem";
    }

    // the format of path is `element-${n}-${m}`, that's why it's split to get to proper element in the array
    const keys = path.split("-").slice(1);
    let currentObject = this.allHeights;
    let maxHeight = this.initialHeight;

    for (let i = 0; i < keys.length; i++) {
      if (keys[i] in currentObject) {
        currentObject = currentObject[keys[i]];
        maxHeight = Math.max(maxHeight, currentObject.value);
      }
    }

    this.currentHeight = maxHeight + "rem";
  };

  //---------- LEVEL 1 ----------
  private toggleMenuMobile() {
    if (!this.isMobileMenuOpen) {
      this.openMenuMobile();
    } else {
      this.closeMenuMobile();
    }
  }

  private openMenuMobile() {
    this.isMobileMenuOpen = true;
    this.addController(this.navMobileLayer, this.closeMenuMobile.bind(this));
  }

  private closeMenuMobile() {
    this.closeNavigationController?.abort();
    this.activeElementLvl1 = "";
    this.activeElementLvl2 = "";
    this.activeElementLvl3 = "";
    this.isMobileMenuOpen = false;
  }

  //---------- LEVEL 2 ----------

  private toggleMenuLvl2(el) {
    if (this.activeElementLvl1 !== el) {
      this.openMenuLvl2(el);
    } else {
      this.closeMenuLvl2();
    }
  }

  private openMenuLvl2(el) {
    this.activeElementLvl1 = el;
    this.addController(this.navLayersLvl2[el], this.closeMenuLvl2.bind(this));
    this.isMobileMenuOpen = true;

    this.setNavigationHeight(this.activeElementLvl1);
  }

  private closeMenuLvl2() {
    const tmpActiveElementLvl1 = this.activeElementLvl1;
    this.activeElementLvl1 = "";
    this.activeElementLvl2 = "";
    this.activeElementLvl3 = "";
    if (isTabletLandscape()) {
      this.addController(this.navMobileLayer, this.closeMenuMobile.bind(this));
      setTimeout(() => {
        this.linksLvl1[tmpActiveElementLvl1].focus();
      });
    } else {
      this.isMobileMenuOpen = false;
    }

    this.setNavigationHeight("");
  }

  //---------- LEVEL 3 ----------

  private toggleMenuLvl3(el) {
    if (this.activeElementLvl2 !== el) {
      this.openMenuLvl3(el);
    } else {
      this.closeMenuLvl3();
    }
  }

  private openMenuLvl3(el) {
    this.activeElementLvl2 = el;
    this.activeElementLvl3 = "";
    if (isTabletLandscape()) {
      this.addController(this.navLayersLvl3[el], this.closeMenuLvl3.bind(this));
    }

    this.setNavigationHeight(this.activeElementLvl2);
  }

  private closeMenuLvl3() {
    const tmpActiveElementLvl2 = this.activeElementLvl2;
    this.activeElementLvl2 = "";
    this.activeElementLvl3 = "";

    if (isTabletLandscape()) {
      this.addController(
        this.navLayersLvl2[this.activeElementLvl1],
        this.closeMenuLvl2.bind(this)
      );
      setTimeout(() => {
        this.linksLvl2[tmpActiveElementLvl2].focus();
      });
    }

    this.setNavigationHeight(this.activeElementLvl1);
  }

  //---------- LEVEL 4 ----------

  private toggleMenuLvl4(el) {
    if (this.activeElementLvl3 !== el) {
      this.openMenuLvl4(el);
    } else {
      this.closeMenuLvl4();
    }
  }

  private openMenuLvl4(el) {
    this.activeElementLvl3 = el;
    if (isTabletLandscape()) {
      this.addController(this.navLayersLvl4[el], this.closeMenuLvl4.bind(this));
    }

    this.setNavigationHeight(this.activeElementLvl3);
  }

  private closeMenuLvl4() {
    const tmpActiveElementLvl3 = this.activeElementLvl3;
    this.activeElementLvl3 = "";

    if (isTabletLandscape()) {
      this.addController(
        this.navLayersLvl3[this.activeElementLvl2],
        this.closeMenuLvl3.bind(this)
      );
      setTimeout(() => {
        this.linksLvl3[tmpActiveElementLvl3].focus();
      });
    }

    this.setNavigationHeight(this.activeElementLvl2);
  }

  //=============== HEIGHT COUNTER =============
  // on desktop the minimum heights required to fit each navigation level is collected into an array
  // when the navigation is used, the correct maximum height of opened items is set in the setNavigationHeight method
  private initialHeight = 2;

  private allHeights = {
    value: this.initialHeight,
  };

  private heightCounter(): void {
    const liHeight = 3.5; // approximate height of one li (in rem)

    const collectHeights = (el: NavigationItems, currentHeightsElement) => {
      if (!el.children?.length) {
        currentHeightsElement.value = this.initialHeight;
      } else {
        const numberOfChildren = el.children?.length || 0;
        const navigationHeight = numberOfChildren * liHeight + 4;
        currentHeightsElement.value = navigationHeight;

        el.children.forEach((childEl: NavigationItems, index: number) => {
          currentHeightsElement[index] = {};
          collectHeights(childEl, currentHeightsElement[index]);
        });
      }
    };

    if (typeof this.navigationItems !== "string") {
      this.navigationItems.forEach((item, index) => {
        this.allHeights[index] = {};
        collectHeights(item, this.allHeights[index]);
      });
    }
  }

  private generatePath = (item) => {
    const path = item.url;
    return this.useContextPath && path.substring(0, 4) !== "http"
      ? config.navContextPath + path
      : path;
  };

  componentWillLoad() {
    this.currentHeight = this.initialHeight + "rem";

    if (typeof this.navigationItems === "string") {
      this.navigationItems = JSON.parse(this.navigationItems);
    }

    this.resizeToTablet = !isTabletLandscape();

    this.heightCounter();
  }

  render() {
    return (
      <Host>
        <f-navigation-trigger
          toggleMenu={() => this.toggleMenuMobile()}
          isMobileMenuOpen={this.isMobileMenuOpen}
        />
        <nav
          class="f-main-navigation"
          id="main-navigation"
          ref={(el) => (this.navMobileLayer = el)}
          data-test="nav"
        >
          <div
            class={{
              "f-main-navigation__track": true,
              "show-menu__lvl-2": this.activeElementLvl1 !== "",
              "show-menu__lvl-3": this.activeElementLvl2 !== "",
              "show-menu__lvl-4": this.activeElementLvl3 !== "",
            }}
            data-test="nav-track"
          >
            <ul class="f-nav__lvl-1" data-test="nav-lvl1">
              {typeof this.navigationItems !== "string" &&
                this.navigationItems.map((iteml1, n) => {
                  return !(iteml1.children && iteml1.children.length) ? (
                    <li class="f-nav__lvl-1__item" data-test="nav-lvl1-li">
                      <a
                        href={this.generatePath(iteml1)}
                        class={{
                          "f-nav__lvl-1__link": true,
                          "is-external": iteml1.external,
                        }}
                        target={iteml1.external ? "_blank" : null}
                        data-test={
                          iteml1.external
                            ? `nav-lvl1-item-link-external-${iteml1.label}`
                            : `nav-lvl1-item-link-${iteml1.label}`
                        }
                      >
                        {iteml1.label}
                        {iteml1.external
                          ? [
                              <f-visually-hidden>
                                {this.externalLinkHiddenText}
                              </f-visually-hidden>,
                              <f-icon iconName="externallink" />,
                            ]
                          : ""}
                      </a>
                    </li>
                  ) : (
                    <li class="f-nav__lvl-1__item" data-test="nav-lvl1-li">
                      <button
                        class={{
                          "f-nav__lvl-1__link": true,
                          "has-sub": true,
                          "is-active":
                            this.activeElementLvl1 === `element-${n}`,
                        }}
                        aria-controls={`f-nav-${n}`}
                        aria-expanded={(
                          this.activeElementLvl1 === `element-${n}`
                        ).toString()}
                        onClick={() => {
                          this.toggleMenuLvl2(`element-${n}`);
                        }}
                        ref={(el) =>
                          (this.linksLvl1[`element-${n}`] =
                            el as HTMLButtonElement)
                        }
                        data-test={`nav-lvl1-item-button-${iteml1.label}`}
                      >
                        {iteml1.label}
                      </button>
                      <div
                        class="f-nav__layer"
                        ref={(el) => (this.navLayersLvl2[`element-${n}`] = el)}
                        id={`f-nav-${n}`}
                      >
                        <div class="l-page-row">
                          <ul
                            class="f-nav__lvl-2"
                            style={{ "min-height": this.currentHeight }}
                            data-test="nav-lvl2"
                          >
                            <li class="f-nav__lvl-2__item f-nav__back">
                              <button
                                class="f-nav__back-btn"
                                onClick={() => {
                                  this.toggleMenuLvl2(`element-${n}`);
                                }}
                                tabindex={
                                  this.activeElementLvl1 === `element-${n}`
                                    ? ""
                                    : "-1"
                                }
                                data-test="nav-lvl2-button-back"
                              >
                                <f-visually-hidden>back</f-visually-hidden>
                                <f-icon iconName="arrow-left" />
                              </button>
                            </li>
                            {iteml1.url !== "" ? (
                              <li
                                class="f-nav__lvl-2__item"
                                data-test="nav-lvl2-li"
                              >
                                <a
                                  class={{
                                    "f-nav__lvl-2__link": true,
                                    "is-external": iteml1.external,
                                  }}
                                  href={this.generatePath(iteml1)}
                                  tabindex={
                                    this.activeElementLvl1 === `element-${n}`
                                      ? ""
                                      : "-1"
                                  }
                                  target={iteml1.external ? "_blank" : null}
                                  data-test={
                                    iteml1.external
                                      ? `nav-lvl2-heading-link-external-${iteml1.label} ${this.teaserLabel}`
                                      : `nav-lvl2-heading-link-${iteml1.label} ${this.teaserLabel}`
                                  }
                                >
                                  {iteml1.label} {this.teaserLabel}
                                  {iteml1.external
                                    ? [
                                        <f-visually-hidden>
                                          {this.externalLinkHiddenText}
                                        </f-visually-hidden>,
                                        <f-icon iconName="externallink" />,
                                      ]
                                    : ""}
                                </a>
                              </li>
                            ) : (
                              ""
                            )}
                            {iteml1.children.map((iteml2, m) => {
                              return !(
                                iteml2.children && iteml2.children.length
                              ) ? (
                                <li
                                  class="f-nav__lvl-2__item"
                                  data-test="nav-lvl2-li"
                                >
                                  <a
                                    href={this.generatePath(iteml2)}
                                    class={{
                                      "f-nav__lvl-2__link": true,
                                      "is-external": iteml2.external,
                                    }}
                                    tabindex={
                                      this.activeElementLvl1 === `element-${n}`
                                        ? ""
                                        : "-1"
                                    }
                                    target={iteml2.external ? "_blank" : null}
                                    data-test={
                                      iteml2.external
                                        ? `nav-lvl2-item-link-external-${iteml2.label}`
                                        : `nav-lvl2-item-link-${iteml2.label}`
                                    }
                                  >
                                    {iteml2.label}
                                    {iteml2.external
                                      ? [
                                          <f-visually-hidden>
                                            {this.externalLinkHiddenText}
                                          </f-visually-hidden>,
                                          <f-icon iconName="externallink" />,
                                        ]
                                      : ""}
                                  </a>
                                </li>
                              ) : (
                                [
                                  <li
                                    class="f-nav__lvl-2__item"
                                    data-test="nav-lvl2-li"
                                  >
                                    <button
                                      class={{
                                        "f-nav__lvl-2__link": true,
                                        "has-sub": true,
                                        "is-active":
                                          this.activeElementLvl2 ===
                                          `element-${n}-${m}`,
                                      }}
                                      onClick={() => {
                                        this.toggleMenuLvl3(
                                          `element-${n}-${m}`
                                        );
                                      }}
                                      ref={(el) =>
                                        (this.linksLvl2[`element-${n}-${m}`] =
                                          el as HTMLButtonElement)
                                      }
                                      tabindex={
                                        this.activeElementLvl1 ===
                                        `element-${n}`
                                          ? ""
                                          : "-1"
                                      }
                                      data-test={`nav-lvl2-item-button-${iteml2.label}`}
                                      aria-controls={`f-nav-${n}-${m}`}
                                      aria-expanded={(
                                        this.activeElementLvl2 ===
                                        `element-${n}-${m}`
                                      ).toString()}
                                    >
                                      {iteml2.label}
                                    </button>
                                    <div
                                      class={{
                                        "f-nav__inner-layer": true,
                                      }}
                                      data-test="nav-lvl3-wrapper"
                                      id={`f-nav-${n}-${m}`}
                                    >
                                      <ul
                                        class="f-nav__lvl-3"
                                        ref={(el) =>
                                          (this.navLayersLvl3[
                                            `element-${n}-${m}`
                                          ] = el)
                                        }
                                        data-test="nav-lvl3"
                                      >
                                        <li
                                          class="f-nav__lvl-3__item f-nav__back"
                                          data-test="nav-lvl3-li"
                                        >
                                          <button
                                            class="f-nav__back-btn"
                                            onClick={() => {
                                              this.toggleMenuLvl3(
                                                `element-${n}-${m}`
                                              );
                                            }}
                                            tabindex={
                                              this.activeElementLvl2 ===
                                              `element-${n}-${m}`
                                                ? ""
                                                : "-1"
                                            }
                                            data-test="nav-lvl3-button-back"
                                          >
                                            <f-visually-hidden>
                                              back
                                            </f-visually-hidden>
                                            <f-icon iconName="arrow-left" />
                                          </button>
                                        </li>

                                        <li
                                          class="f-nav__lvl-3__item"
                                          data-test="nav-lvl3-li"
                                        >
                                          {iteml2.url !== "" ? (
                                            <a
                                              class={{
                                                "f-nav__inner-layer__heading":
                                                  true,
                                                "f-nav__lvl-3__link": true,
                                                "is-external": iteml2.external,
                                              }}
                                              href={this.generatePath(iteml2)}
                                              tabindex={
                                                this.activeElementLvl2 ===
                                                `element-${n}-${m}`
                                                  ? ""
                                                  : "-1"
                                              }
                                              target={
                                                iteml2.external
                                                  ? "_blank"
                                                  : null
                                              }
                                              data-test={
                                                iteml2.external
                                                  ? `nav-lvl3-heading-link-external-${iteml2.label} ${this.teaserLabel}`
                                                  : `nav-lvl3-heading-link-${iteml2.label} ${this.teaserLabel}`
                                              }
                                            >
                                              {iteml2.label} {this.teaserLabel}
                                              {iteml2.external
                                                ? [
                                                    <f-visually-hidden>
                                                      {
                                                        this
                                                          .externalLinkHiddenText
                                                      }
                                                    </f-visually-hidden>,
                                                    <f-icon iconName="externallink" />,
                                                  ]
                                                : ""}
                                            </a>
                                          ) : (
                                            <p
                                              class="f-nav__inner-layer__heading"
                                              data-test="nav-lvl3-heading-label"
                                            >
                                              {iteml2.label} {this.teaserLabel}
                                            </p>
                                          )}
                                        </li>

                                        {iteml2.children.map((iteml3, l) => {
                                          return !(
                                            iteml3.children &&
                                            iteml3.children.length
                                          ) ? (
                                            <li
                                              class="f-nav__lvl-3__item"
                                              data-test="nav-lvl3-li"
                                            >
                                              <a
                                                href={this.generatePath(iteml3)}
                                                class={{
                                                  "f-nav__lvl-3__link": true,
                                                  "is-external":
                                                    iteml3.external,
                                                }}
                                                tabindex={
                                                  this.activeElementLvl2 ===
                                                  `element-${n}-${m}`
                                                    ? ""
                                                    : "-1"
                                                }
                                                target={
                                                  iteml3.external
                                                    ? "_blank"
                                                    : null
                                                }
                                                data-test={
                                                  iteml3.external
                                                    ? `nav-lvl3-item-link-external-${iteml3.label}`
                                                    : `nav-lvl3-item-link-${iteml3.label}`
                                                }
                                              >
                                                {iteml3.label}
                                                {iteml3.external
                                                  ? [
                                                      <f-visually-hidden>
                                                        {
                                                          this
                                                            .externalLinkHiddenText
                                                        }
                                                      </f-visually-hidden>,
                                                      <f-icon iconName="externallink" />,
                                                    ]
                                                  : ""}
                                              </a>
                                            </li>
                                          ) : (
                                            [
                                              <li
                                                class="f-nav__lvl-3__item"
                                                data-test="nav-lvl3-li"
                                              >
                                                <button
                                                  class={{
                                                    "f-nav__lvl-3__link": true,
                                                    "has-sub": true,
                                                    "is-active":
                                                      this.activeElementLvl3 ===
                                                      `element-${n}-${m}-${l}`,
                                                  }}
                                                  onClick={() => {
                                                    this.toggleMenuLvl4(
                                                      `element-${n}-${m}-${l}`
                                                    );
                                                  }}
                                                  ref={(el) =>
                                                    (this.linksLvl3[
                                                      `element-${n}-${m}-${l}`
                                                    ] = el as HTMLButtonElement)
                                                  }
                                                  tabindex={
                                                    this.activeElementLvl2 ===
                                                    `element-${n}-${m}`
                                                      ? ""
                                                      : "-1"
                                                  }
                                                  data-test={`nav-lvl3-item-button-${iteml3.label}`}
                                                  aria-controls={`f-nav-${m}-${l}`}
                                                  aria-expanded={(
                                                    this.activeElementLvl3 ===
                                                    `element-${n}-${m}-${l}`
                                                  ).toString()}
                                                >
                                                  {iteml3.label}
                                                </button>
                                                <div
                                                  class={{
                                                    "f-nav__inner-layer-lvl-4":
                                                      true,
                                                  }}
                                                  data-test="nav-lvl4-wrapper"
                                                  id={`f-nav-${m}-${l}`}
                                                >
                                                  <ul
                                                    class="f-nav__lvl-4"
                                                    ref={(el) =>
                                                      (this.navLayersLvl4[
                                                        `element-${n}-${m}-${l}`
                                                      ] = el)
                                                    }
                                                    data-test="nav-lvl4"
                                                  >
                                                    <li
                                                      class="f-nav__lvl-4__item f-nav__back"
                                                      data-test="nav-lvl4-li"
                                                    >
                                                      <button
                                                        class="f-nav__back-btn"
                                                        onClick={() => {
                                                          this.toggleMenuLvl4(
                                                            `element-${n}-${m}-${l}`
                                                          );
                                                        }}
                                                        tabindex={
                                                          this
                                                            .activeElementLvl3 ===
                                                          `element-${n}-${m}-${l}`
                                                            ? ""
                                                            : "-1"
                                                        }
                                                        data-test="nav-lvl4-button-back"
                                                      >
                                                        <f-visually-hidden>
                                                          back
                                                        </f-visually-hidden>
                                                        <f-icon iconName="arrow-left" />
                                                      </button>
                                                    </li>
                                                    <li
                                                      class="f-nav__lvl-4__item"
                                                      data-test="nav-lvl4-li"
                                                    >
                                                      {iteml3.url !== "" ? (
                                                        <a
                                                          class={{
                                                            "f-nav__inner-layer__heading":
                                                              true,
                                                            "f-nav__lvl-4__link":
                                                              true,
                                                            "is-external":
                                                              iteml3.external,
                                                          }}
                                                          href={this.generatePath(
                                                            iteml3
                                                          )}
                                                          tabindex={
                                                            this
                                                              .activeElementLvl3 ===
                                                            `element-${n}-${m}-${l}`
                                                              ? ""
                                                              : "-1"
                                                          }
                                                          target={
                                                            iteml3.external
                                                              ? "_blank"
                                                              : null
                                                          }
                                                          data-test={
                                                            iteml3.external
                                                              ? `nav-lvl4-heading-link-external-${iteml3.label} ${this.teaserLabel}`
                                                              : `nav-lvl4-heading-link-${iteml3.label} ${this.teaserLabel}`
                                                          }
                                                        >
                                                          {iteml3.label}{" "}
                                                          {this.teaserLabel}
                                                          {iteml3.external
                                                            ? [
                                                                <f-visually-hidden>
                                                                  {
                                                                    this
                                                                      .externalLinkHiddenText
                                                                  }
                                                                </f-visually-hidden>,
                                                                <f-icon iconName="externallink" />,
                                                              ]
                                                            : ""}
                                                        </a>
                                                      ) : (
                                                        <p
                                                          class="f-nav__inner-layer__heading"
                                                          data-test="nav-lvl4-heading-label"
                                                        >
                                                          {iteml3.label}{" "}
                                                          {this.teaserLabel}
                                                        </p>
                                                      )}
                                                    </li>
                                                    {iteml3.children.map(
                                                      (iteml4) => {
                                                        return (
                                                          <li
                                                            class="f-nav__lvl-4__item"
                                                            data-test="nav-lvl4-li"
                                                          >
                                                            <a
                                                              href={this.generatePath(
                                                                iteml4
                                                              )}
                                                              class={{
                                                                "f-nav__lvl-4__link":
                                                                  true,
                                                                "is-external":
                                                                  iteml4.external,
                                                              }}
                                                              tabindex={
                                                                this
                                                                  .activeElementLvl3 ===
                                                                `element-${n}-${m}-${l}`
                                                                  ? ""
                                                                  : "-1"
                                                              }
                                                              target={
                                                                iteml4.external
                                                                  ? "_blank"
                                                                  : null
                                                              }
                                                              data-test={
                                                                iteml4.external
                                                                  ? `nav-lvl4-item-link-external-${iteml4.label}`
                                                                  : `nav-lvl4-item-link-${iteml4.label}`
                                                              }
                                                            >
                                                              {iteml4.label}
                                                              {iteml4.external
                                                                ? [
                                                                    <f-visually-hidden>
                                                                      {
                                                                        this
                                                                          .externalLinkHiddenText
                                                                      }
                                                                    </f-visually-hidden>,
                                                                    <f-icon iconName="externallink" />,
                                                                  ]
                                                                : ""}
                                                            </a>
                                                          </li>
                                                        );
                                                      }
                                                    )}
                                                  </ul>
                                                </div>
                                              </li>,
                                            ]
                                          );
                                        })}
                                      </ul>
                                    </div>
                                  </li>,
                                ]
                              );
                            })}
                          </ul>
                          <div
                            class="f-nav__lang-selector-wrapper f-nav__lang-selector-wrapper--desktop"
                            data-test="nav-lang-selector-wrapper-desktop"
                          >
                            <f-language-selector />
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
            </ul>
            <div
              class="f-nav__lang-selector-wrapper f-nav__lang-selector-wrapper--tablet"
              data-test="nav-lang-selector-wrapper-tablet"
            >
              <f-language-selector />
            </div>
          </div>
        </nav>
      </Host>
    );
  }
}
